import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, distinctUntilChanged, filter, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { combineLatest, of } from 'rxjs';

import { equals } from '@fcom/core/utils';

import { initialState } from '../../store/reducers/data.reducer';
import { TimetableSearchService } from '../../services/timetable-search.service';
import { DataActions, SearchActions, StatusActions } from '../actions';
import { selectData, selectSearch } from '../selectors';
import { SearchState, TimetableFeatureState, TimetableStatus } from '../../interfaces/index';
import { getEarliestSearchDayInMonth, getLastDayInMonth } from '../../utils/timetable.utils';

const hasNewSearch = (prev: SearchState, next: SearchState): boolean => {
  if (!next.origin && !next.destination && !next.directOnly) {
    return false;
  }

  return prev.destination !== next.destination || prev.origin !== next.origin || prev.directOnly !== next.directOnly;
};

@Injectable()
export class DataEffects {
  loadTimetables$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SearchActions.setSearch.type),
        withLatestFrom(
          combineLatest([
            this.store$.pipe(selectSearch()),
            this.store$.pipe(selectData()).pipe(map((data) => data.flights)),
          ])
        ),
        distinctUntilChanged(([prev], [next, [_search, results]]) => {
          if (equals(prev, next)) {
            return true;
          }

          if (results && hasNewSearch(prev, next)) {
            this.store$.dispatch(DataActions.clearResults());
            this.store$.dispatch(SearchActions.setSearch({}));

            return true;
          }

          return false;
        }),
        filter(([_changes, [search, results]]) => {
          const firstDay = getEarliestSearchDayInMonth(search.selectedMonth).toString();
          const lastDay = getLastDayInMonth(search.selectedMonth).toString();

          if (results && results[firstDay] && results[lastDay]) {
            return false;
          }

          return Boolean(search.origin && search.destination && search.selectedMonth);
        }),
        map(([_changes, [search]]) => {
          return {
            ...search,
            searchDay: getEarliestSearchDayInMonth(search.selectedMonth),
          };
        }),
        tap(() => {
          this.store$.dispatch(StatusActions.setStatus({ status: TimetableStatus.LOADING }));
        }),
        mergeMap((search) =>
          this.timetableSearchService.getTimetable(search).pipe(
            map((timetable) => {
              this.store$.dispatch(DataActions.setResults(timetable));
              this.store$.dispatch(StatusActions.setStatus({ status: TimetableStatus.READY }));
            }),
            catchError(() => {
              this.store$.dispatch(DataActions.setResults(initialState));
              this.store$.dispatch(StatusActions.setStatus({ status: TimetableStatus.ERROR }));

              return of(null);
            })
          )
        )
      ),
    {
      dispatch: false,
    }
  );

  constructor(
    private store$: Store<TimetableFeatureState>,
    private actions$: Actions,
    private timetableSearchService: TimetableSearchService
  ) {}
}
